/* eslint-disable react/jsx-props-no-spreading */
import React, { Suspense, lazy } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Home from 'pages/home';
import FooterRoutes from 'components/routes/FooterRoutes';
import FooterRouteStatic from 'components/routes/FooterRouteStatic';
import EmptyRoutes from 'components/routes/EmptyRoutes';
import Loading from 'components/shared/Loading';
import NewLayout from 'components/layout/NewLayout';
import Spinner from 'components/shared/Spinner';

import {
  ABOUT,
  ACCOUNT_SETTINGS,
  ACCOUNT_SUMMARY,
  BOOKING_CONFIRMATION,
  BOOKINGS_ID,
  BOOKINGS_INVITE,
  BOOKINGS,
  BOOKING_LIMIT_REACHED,
  CHECKOUT,
  CITY_SEARCH_RESULT,
  COMMUNICATION_PREFERENCES,
  CONFIRM_DELETION,
  COURSE_DETAILS,
  COURSE_REVIEW,
  COURSE_REVIEWS,
  COURSE_SEARCH_RESULT,
  DEAL,
  DEALS,
  DELETE_ACCOUNT,
  DISCLAIMERS,
  EDIT_PAYMENT_METHOD,
  EXPLORE_COUNTRY,
  EXPLORE_STATE,
  EVENTUAL_WEATHER,
  FAQS,
  FAVORITES,
  FREE_MARKETPLACE,
  FREE_MARKETPLACE_SIGNUP_DETAILS,
  GOLF_COURSE_RESOURCES,
  GOLFER_STATS,
  GOLFER_STATS_EDIT,
  HOME,
  MESSAGE_PREFERENCES,
  MEMBERSHIP_UPSELL,
  MODERATOR_REVIEWS,
  MY_MEMBERSHIP,
  MY_PROFILE,
  MY_SG_CLUB,
  NETWORK_MEMBERSHIP_BENEFITS,
  NETWORK_MEMBERSHIP_COURSES,
  NETWORK_MEMBERSHIP_SALES,
  NETWORK_PARTICIPATING_COURSES,
  NEW_COURSE_REVIEW,
  NEW_PAYMENT_METHOD,
  PASSWORD_EDIT,
  PAYMENT_METHODS,
  PERSONAL_INFO,
  PRICE_ALERTS,
  POLICY_AND_TERMS,
  PRIVACY_POLICY,
  RATE_TYPES,
  REQUEST_RESET_PASSWORD,
  RESET_PASSWORD,
  REWARDS,
  SECTION_COURSES,
  SELECT_PROVIDER,
  SG_CLUB,
  SG_CLUB_TERMS_AND_CONDITIONS,
  MEMBERSHIP_TERMS_AND_CONDITIONS,
  SIGN_IN,
  SIGN_UP,
  SUPPORT,
  TEE_TIME_ERROR,
  TEE_TIME_NOT_ALLOWED,
  TEE_TIME_NOT_AVAILABLE,
  TERMS_AND_CONDITIONS,
  UNLOCK,
  VERIFY_CUSTOMER_DETAILS,
  VIP_PERKS,
  WELCOME_TO_SUPREME,
  UNLOCK_ACCOUNT_FAILED,
  NOT_FOUND,
  UNSUBSCRIBE,
  UNSUBSCRIBE_PRE_USERS,
  REWARDS_TERMS_AND_CONDITIONS,
  GOLF_TIME_PROMOTIONS,
  NETWORK_MEMBERSHIP_JOIN,
  NETWORK_OFFERS,
  NETWORK_OFFERS_REDEEM,
  WEB_VIEW_DONE,
  ANONYMOUS_PURCHASE,
  GOLF_VANTAGE_PROMOTION,
  PARTNER_OFFERS,
  NETWORK_PARTICIPATING_PARTNERS,
  TEE_TIME_RATES_BLOG,
  SUPER_BOWL_BLOG,
  STATE_SEARCH_RESULT,
  PERFORM_APP_ACTION,
  EVENTUAL_WEATHER_PURCHASE,
} from 'utils/routes';
import { features } from 'utils/features';
import buildStaticPage from 'utils/buildStaticPage';
import buildLegalStaticPage from 'utils/buildLegalStaticPage';
import { componentLoader } from 'utils/componentLoader';
import ScrollToTop from 'hooks/ScrollToTop';
import { useScreen } from 'hooks/useDimensions';

const ReactLazyPreload = (importStatement) => {
  const Component = lazy(() => componentLoader(importStatement));
  Component.preload = importStatement;
  return Component;
};

const AccountSettings = ReactLazyPreload(() => import(/* webpackChunkName: "AccountSettings" */ 'components/AccountSettings'));
const AccountSummary = ReactLazyPreload(() => import(/* webpackChunkName: "MyProfile" */ 'components/AccountSummary'));
const BookingConfirmation = ReactLazyPreload(() => import(/* webpackChunkName: "BookingConfirmation" */ 'pages/bookingConfirmation'));
const Bookings = ReactLazyPreload(() => import(/* webpackChunkName: "Bookings" */ 'components/mySG/Bookings'));
const BookingsInvite = ReactLazyPreload(() => import(/* webpackChunkName: "BookingInvite" */ 'components/BookingsInvite'));
const Checkout = ReactLazyPreload(() => import(/* webpackChunkName: "Checkout" */ 'pages/checkout'));
const CommunicationPreferences = ReactLazyPreload(() => import(/* webpackChunkName: "CommunicationPreferences" */ 'components/myAccount/communicationPreferences/CommunicationPreferencesForm'));
const CourseOperators = ReactLazyPreload(() => import(/* webpackChunkName: "courseOperators" */ 'components/CourseOperators'));
const CourseReviews = ReactLazyPreload(() => import(/* webpackChunkName: "courseReviews" */ 'pages/courseReviews'));
const Courses = ReactLazyPreload(() => import(/* webpackChunkName: "Courses" */ 'pages/courses'));
const CourseDetails = ReactLazyPreload(() => import(/* webpackChunkName: "CourseDetails" */ 'pages/courseDetails'));
const TeeTimes = ReactLazyPreload(() => import(/* webpackChunkName: "teeTimes" */ 'pages/teeTimes'));
const CreditCards = ReactLazyPreload(() => import(/* webpackChunkName: "CreditCards" */ 'components/mySG/CreditCards'));
const DeleteUser = ReactLazyPreload(() => import(/* webpackChunkName: "DeleteUser" */ 'components/deleteUser/DeleteUser'));
const Explore = ReactLazyPreload(() => import(/* webpackChunkName: "Explore" */ 'pages/explore'));
const EditCreditCard = ReactLazyPreload(() => import(/* webpackChunkName: "EditCreditCard" */ 'components/EditCreditCard'));
const EventualWeather = ReactLazyPreload(() => import(/* webpackChunkName: "EventualWeather" */ 'pages/eventualWeather/EventualWeather'));
const EventualWeatherPurchase = ReactLazyPreload(() => import(/* webpackChunkName: "EventualWeatherPurchase" */ 'pages/eventualWeather/EventualWeatherPurchase'));
const Favorites = ReactLazyPreload(() => import(/* webpackChunkName: "Favorites" */ 'components/mySG/Favorites'));
const GolferStats = ReactLazyPreload(() => import(/* webpackChunkName: "GolferStats" */ 'components/GolferStats'));
const GolferStatsEdit = ReactLazyPreload(() => import(/* webpackChunkName: "GolferStatsEdit" */ 'components/GolferStatsEdit'));
const GolfvantagePromotion = ReactLazyPreload(() => import(/* webpackChunkName: "GolfvantagePromotion" */ 'pages/networkMembership/GolfvantagePromotion'));
const Login = ReactLazyPreload(() => import(/* webpackChunkName: "Login" */ 'components/shared/Login'));
const MembershipUpsell = ReactLazyPreload(() => import(/* webpackChunkName: "MembershipUpsell" */ 'components/MembershipUpsell'));
const ModeratorReviews = ReactLazyPreload(() => import(/* webpackChunkName: "ModeratorReviews" */ 'components/myAccount/ModeratorReviews'));
const MyMembership = ReactLazyPreload(() => import(/* webpackChunkName: "MyMembership" */ 'components/MyMembership'));
const MyOffers = ReactLazyPreload(() => import(/* webpackChunkName: "MyOffers" */ 'components/MyOffers'));
const MyOffersRedeem = ReactLazyPreload(() => import(/* webpackChunkName: "MyOffersRedeem" */ 'components/MyOffersRedeem'));
const MyProfile = ReactLazyPreload(() => import(/* webpackChunkName: "MyProfile" */ 'components/MyProfile'));
const MySGRoutes = ReactLazyPreload(() => import(/* webpackChunkName: "MySGRoutes" */ 'components/routes/MySGRoutes'));
const NetworkMembershipBenefits = ReactLazyPreload(() => import(/* webpackChunkName: "NetworkMembershipBenefits" */ 'components/shared/ui/modals/MembershipBenefitsModal'));
const NetworkMembershipCourses = ReactLazyPreload(() => import(/* webpackChunkName: "NetworkMembershipCourses" */ 'pages/networkMembership/MembershipCourses'));
const NetworkMembershipSales = ReactLazyPreload(() => import(/* webpackChunkName: "NetworkMembershipSales" */ 'pages/networkMembership/MembershipSales'));
const NetworkMembershipPurchase = ReactLazyPreload(() => import(/* webpackChunkName: "NetworkMembershipPurchase" */ 'pages/networkMembership/MembershipPurchase'));
const AddNewReview = ReactLazyPreload(() => import(/* webpackChunkName: "AddNewReview" */ 'pages/courseReviews/AddNewReview'));
const NewCreditCard = ReactLazyPreload(() => import(/* webpackChunkName: "NewCreditCard" */ 'components/mySG/NewCreditCard'));
const NotFoundPage = ReactLazyPreload(() => import(/* webpackChunkName: "NotFoundPage" */ 'components/NotFoundPage'));
const PasswordEdit = ReactLazyPreload(() => import(/* webpackChunkName: "PasswordEdit" */ 'components/PasswordEdit'));
const PartnerOffers = ReactLazyPreload(() => import(/* webpackChunkName: "PartnerOffers" */ 'pages/networkMembership/PartnerOffers'));
const PartnersPromotion = ReactLazyPreload(() => import(/* webpackChunkName: "PartnersPromotion" */ 'pages/networkMembership/PartnersPromotion'));
const PriceAlerts = ReactLazyPreload(() => import(/* webpackChunkName: "PriceAlerts" */ 'components/mySG/PriceAlerts'));
const PolicyAndTerms = ReactLazyPreload(() => import(/* webpackChunkName: "PolicyAndTerms" */ 'components/PolicyAndTerms'));
const PersonalInfo = ReactLazyPreload(() => import(/* webpackChunkName: "PersonalInfo" */ 'components/PersonalInfo'));
const RateTypes = ReactLazyPreload(() => import(/* webpackChunkName: "RateTypes" */ 'pages/rateTypes'));
const RequestResetPassword = ReactLazyPreload(() => import(/* webpackChunkName: "RequestResetPassword" */ 'components/shared/RequestResetPassword'));
const ResetPassword = ReactLazyPreload(() => import(/* webpackChunkName: "ResetPassword" */ 'components/shared/ResetPassword'));
const RewardsFullProfile = ReactLazyPreload(() => import(/* webpackChunkName: "RewardsFullProfile" */ 'components/mySG/RewardsFullProfile'));
const SelectProvider = ReactLazyPreload(() => import(/* webpackChunkName: "SelectProvider" */ 'pages/providers'));
const SignUp = ReactLazyPreload(() => import(/* webpackChunkName: "SignUp" */ 'components/signup/SignUp'));
const Support = ReactLazyPreload(() => import(/* webpackChunkName: "Support" */ 'components/Support'));
const SgClub = ReactLazyPreload(() => import(/* webpackChunkName: "SgClub" */ 'components/SgClub'));
const SgClubDetails = ReactLazyPreload(() => import(/* webpackChunkName: "SgClubDetails" */ 'components/mySG/sgClub/SgClubDetails'));
const StateListing = ReactLazyPreload(() => import(/* webpackChunkName: "StateListing" */ 'pages/courses/StateListing'));
const TeeTimeError = ReactLazyPreload(() => import(/* webpackChunkName: "TeeTimeError" */ 'components/shared/teeTimeError/TeeTimeError'));
const TestSearch = ReactLazyPreload(() => import(/* webpackChunkName: "TestSearch" */ 'components/TestSearch'));
const UnderMaintenance = ReactLazyPreload(() => import(/* webpackChunkName: "UnderMaintenance" */ 'components/UnderMaintenance'));
const Unlock = ReactLazyPreload(() => import(/* webpackChunkName: "Unlock" */ 'components/shared/Unlock'));
const VerifyCustomerDetails = ReactLazyPreload(() => import(/* webpackChunkName: "VerifyCustomerDetails" */ 'components/VerifyCustomerDetails'));
const WelcomeToSupreme = ReactLazyPreload(() => import(/* webpackChunkName: "WelcomeToSupreme" */ 'components/WelcomeToSupreme'));
const UnlockAccountFailed = ReactLazyPreload(() => import(/* webpackChunkName: "UnlockAccountFailed" */ 'components/UnlockAccountFailed'));
const UnsubscribeFromMail = ReactLazyPreload(() => import(/* webpackChunkName: "UnsubscribeFromMail" */ 'components/UnsubscribeFromMail'));
const HeaderMenu = ReactLazyPreload(() => import(/* webpackChunkName: "HeaderMenu" */ 'components/headerMenu/HeaderMenu'));
const WebViewDone = ReactLazyPreload(() => import(/* webpackChunkName: "WebViewDone" */ 'components/WebViewDone'));
const AnonymousPurchase = ReactLazyPreload(() => import(/* webpackChunkName: "AnonymousPurchase" */ 'components/AnonymousPurchase'));
const WebViewRedirect = ReactLazyPreload(() => import(/* webpackChunkName: "WebViewRedirect" */ 'pages/webview/WebViewRedirect'));

// Blogs
const TeeTimeRatesBlog = ReactLazyPreload(() => import(/* webpackChunkName: "TeeTimeRates" */ 'pages/blogs/TeeTimeRates'));
const SuperBowlBlog = ReactLazyPreload(() => import(/* webpackChunkName: "SuperBowl" */ 'pages/blogs/SuperBowl'));

export const routes = [
  /* ########################################################## */
  /* ### Non-dynamic zero-part routes alphabetically sorted ### */
  /* ########################################################## */
  {
    useNewLayout: true,
    exact: true,
    path: HOME,
    component: Home,
    navbarProps: {
      wrapperStyles: 'z-50 w-full flex justify-center h-14 px-4 py-2 md:fixed top-0 md:h-18 md:px-10 lg:h-auto lg:px-14 lg:py-4',
    },
    preloadComponent: () => Courses.preload(),
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: '/test-new-search-logic',
    component: TestSearch,
    withDownloadsSection: true,
    withSubScenery: true,
  },
  {
    useNewLayout: true,
    exact: true,
    path: TEE_TIME_RATES_BLOG,
    component: features.showTeeTimesBlog ? TeeTimeRatesBlog : NotFoundPage,
  },
  {
    useNewLayout: true,
    exact: true,
    path: SUPER_BOWL_BLOG,
    component: features.showSuperBowlBlog ? SuperBowlBlog : NotFoundPage,
  },
  /* ########################################################## */
  /* ### Non dynamic one-part routes alphabetically sorted #### */
  /* ########################################################## */
  {
    layout: FooterRoutes,
    exact: true,
    path: ABOUT,
    component: buildStaticPage({ slug: 'v2-about', pageTitle: 'About Us' }),
  },
  {
    useNewLayout: true,
    exact: true,
    path: BOOKING_LIMIT_REACHED,
    component: TeeTimeError,
    withNavbar: false,
  },
  {
    useNewLayout: true,
    exact: true,
    path: COURSE_REVIEW,
    component: CourseReviews,
  },
  {
    useNewLayout: true,
    exact: true,
    path: COURSE_REVIEWS,
    component: CourseReviews,
  },
  {
    useNewLayout: true,
    exact: true,
    path: DEALS,
    component: features.dealsPage ? Spinner : NotFoundPage,
  },
  {
    useNewLayout: true,
    exact: true,
    path: EVENTUAL_WEATHER,
    component: EventualWeather,
  },
  {
    useNewLayout: true,
    exact: true,
    path: EVENTUAL_WEATHER_PURCHASE,
    component: EventualWeatherPurchase,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: REQUEST_RESET_PASSWORD,
    component: RequestResetPassword,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: RESET_PASSWORD,
    component: ResetPassword,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: VERIFY_CUSTOMER_DETAILS,
    component: VerifyCustomerDetails,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: SIGN_IN,
    component: Login,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: SIGN_UP,
    component: SignUp,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: UNLOCK,
    component: Unlock,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: UNSUBSCRIBE,
    component: UnsubscribeFromMail,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: UNSUBSCRIBE_PRE_USERS,
    component: UnsubscribeFromMail,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: SUPPORT,
    component: Support,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: SG_CLUB,
    component: features.membership ? SgClub : NotFoundPage,
    withDownloadsSection: true,
  },
  {
    useNewLayout: true,
    exact: true,
    path: TEE_TIME_NOT_AVAILABLE,
    component: TeeTimeError,
    withNavbar: false,
  },
  {
    useNewLayout: true,
    component: TeeTimeError,
    exact: true,
    path: TEE_TIME_ERROR,
    withNavbar: false,
  },
  {
    useNewLayout: true,
    exact: true,
    path: TEE_TIME_NOT_ALLOWED,
    component: TeeTimeError,
    withNavbar: false,
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: FAQS,
    component: buildLegalStaticPage({
      slug: 'v2-faqs',
      pageTitle: 'Frequently Asked Questions',
    }),
    backTo: 'previous',
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: POLICY_AND_TERMS,
    component: PolicyAndTerms,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: PRIVACY_POLICY,
    component: buildLegalStaticPage({
      slug: 'v2-privacy-policy',
      pageTitle: 'Privacy Policy',
    }),
    backTo: 'previous',
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: TERMS_AND_CONDITIONS,
    component: buildLegalStaticPage({
      slug: 'v2-terms-conditions',
      pageTitle: 'Terms and Conditions',
    }),
    backTo: 'previous',
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: SG_CLUB_TERMS_AND_CONDITIONS,
    component: features.membership ? buildLegalStaticPage({
      slug: 'v2-sg-club-terms-conditions',
      pageTitle: 'SG Club Terms and Conditions',
    }) : NotFoundPage,
    backTo: 'previous',
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: MEMBERSHIP_TERMS_AND_CONDITIONS,
    component: features.networkMembership ? buildLegalStaticPage({
      slug: 'v2-membership-terms-and-conditions',
      pageTitle: 'Membership Terms and Conditions',
    }) : NotFoundPage,
    backTo: 'previous',
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: DISCLAIMERS,
    component: features.disclaimerPage ? buildLegalStaticPage({
      slug: 'v2-disclaimers',
      pageTitle: 'Disclaimers',
    }) : NotFoundPage,
    backTo: 'previous',
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: REWARDS_TERMS_AND_CONDITIONS,
    component: features.offers ? buildLegalStaticPage({
      slug: 'v2-rewards-terms-and-conditions',
      pageTitle: 'Rewards',
    }) : NotFoundPage,
    backTo: 'previous',
  },
  {
    layout: FooterRouteStatic,
    exact: true,
    path: GOLF_TIME_PROMOTIONS,
    component: features?.golfTimePromotions ? buildLegalStaticPage({
      slug: 'golf-time-promotions',
      pageTitle: 'Golf Time Promotions',
    }) : NotFoundPage,
    backTo: 'previous',
  },
  {
    useNewLayout: true,
    exact: true,
    path: WELCOME_TO_SUPREME,
    component: features.marketingCommunication ? WelcomeToSupreme : NotFoundPage,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: FREE_MARKETPLACE,
    component: features.courseOperatorsFreeMarketplace ? CourseOperators : NotFoundPage,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: FREE_MARKETPLACE_SIGNUP_DETAILS,
    component:
      features.courseOperatorsFreeMarketplaceSignupDetails ? CourseOperators : NotFoundPage,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: VIP_PERKS,
    component: CourseOperators,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: GOLF_COURSE_RESOURCES,
    component: features.courseOperatorsGolfCourseResources ? CourseOperators : NotFoundPage,
  },
  {
    useNewLayout: true,
    exact: true,
    path: PERFORM_APP_ACTION,
    component: WebViewRedirect,
    withNavbar: false,
    withFooter: false,
  },
  /* ########################################################## */
  /* ### Non dynamic two-part routes alphabetically sorted #### */
  /* ########################################################## */
  {
    layout: MySGRoutes,
    exact: true,
    path: ACCOUNT_SETTINGS,
    component: AccountSettings,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: ACCOUNT_SUMMARY,
    component: AccountSummary,
    backTo: '/',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: PARTNER_OFFERS,
    component: PartnerOffers,
    backTo: MY_MEMBERSHIP,
    backToMobile: MY_MEMBERSHIP,
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: PERSONAL_INFO,
    component: PersonalInfo,
    backTo: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: COMMUNICATION_PREFERENCES,
    component: features.marketingCommunication ? CommunicationPreferences : NotFoundPage,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-settings',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: MESSAGE_PREFERENCES,
    component: features.marketingCommunication ? CommunicationPreferences : NotFoundPage,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: NETWORK_MEMBERSHIP_COURSES,
    component: NetworkMembershipCourses,
    backTo: MY_MEMBERSHIP,
    backToMobile: MY_MEMBERSHIP,
    withFooter: false,
    useMobileLayout: true,
  },
  {
    useNewLayout: true,
    exact: true,
    path: NETWORK_PARTICIPATING_COURSES,
    component: NetworkMembershipCourses,
  },
  {
    useNewLayout: true,
    exact: true,
    path: NETWORK_MEMBERSHIP_SALES,
    component: NetworkMembershipSales,
  },
  {
    useNewLayout: true,
    exact: true,
    path: GOLF_VANTAGE_PROMOTION,
    component: GolfvantagePromotion,
  },
  {
    useNewLayout: true,
    exact: true,
    path: NETWORK_PARTICIPATING_PARTNERS,
    component: PartnersPromotion,
  },
  {
    useNewLayout: true,
    exact: true,
    path: NETWORK_MEMBERSHIP_BENEFITS,
    component: NetworkMembershipBenefits,
    withFooter: false,
  },
  {
    useNewLayout: true,
    exact: true,
    path: NETWORK_MEMBERSHIP_JOIN,
    component: NetworkMembershipPurchase,
  },
  {
    useNewLayout: true,
    exact: true,
    path: WEB_VIEW_DONE,
    component: WebViewDone,
    withNavbar: false,
    withFooter: false,
  },
  {
    useNewLayout: true,
    exact: true,
    path: ANONYMOUS_PURCHASE,
    component: AnonymousPurchase,
    withNavbar: false,
    withFooter: false,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: CONFIRM_DELETION,
    component: features.deleteUser ? DeleteUser : NotFoundPage,
  },
  {
    layout: FooterRoutes,
    mobileLayout: MySGRoutes,
    exact: true,
    path: DELETE_ACCOUNT,
    component: features.deleteUser ? DeleteUser : NotFoundPage,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-settings',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: FAVORITES,
    component: Favorites,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: GOLFER_STATS,
    component: GolferStats,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: GOLFER_STATS_EDIT,
    component: GolferStatsEdit,
    backTo: '/my-account/golfer-stats',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: MODERATOR_REVIEWS,
    component: features.moderator ? ModeratorReviews : NotFoundPage,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: MY_MEMBERSHIP,
    component: MyMembership,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    withFooter: false,
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: NETWORK_OFFERS,
    component: MyOffers,
    withFooter: false,
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: NETWORK_OFFERS_REDEEM,
    component: MyOffersRedeem,
    withFooter: false,
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: MY_PROFILE,
    component: MyProfile,
    backTo: '/',
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: MY_SG_CLUB,
    component: features.membership ? SgClubDetails : NotFoundPage,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    useNewLayout: true,
    exact: true,
    path: NEW_COURSE_REVIEW,
    component: AddNewReview,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: PASSWORD_EDIT,
    component: PasswordEdit,
    backTo: '/my-account/account-settings',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: EDIT_PAYMENT_METHOD,
    component: EditCreditCard,
    backTo: '/my-account/payment-methods',
    backToMobile: '/my-account/payment-methods',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: PAYMENT_METHODS,
    component: CreditCards,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-settings',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: PRICE_ALERTS,
    component: features.priceAlerts ? PriceAlerts : NotFoundPage,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: REWARDS,
    component: RewardsFullProfile,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  {
    layout: MySGRoutes,
    exact: true,
    path: BOOKINGS,
    component: Bookings,
    backTo: '/my-account/my-profile',
    backToMobile: '/my-account/account-summary',
    useMobileLayout: true,
  },
  /* ########################################################## */
  /* ### Non dynamic three-part routes alphabetically sorted ## */
  /* ########################################################## */
  {
    layout: MySGRoutes,
    exact: true,
    path: NEW_PAYMENT_METHOD,
    component: NewCreditCard,
    backTo: '/my-account/payment-methods',
    backToMobile: '/my-account/payment-methods',
    useMobileLayout: true,
  },
  /* ########################################################## */
  /* ### Dynamic one-part routes sorted by specificity ######## */
  /* ########################################################## */
  {
    useNewLayout: true,
    exact: true,
    path: EXPLORE_COUNTRY,
    component: Explore,
    withNavbar: false,
  },
  {
    useNewLayout: true,
    exact: true,
    path: BOOKING_CONFIRMATION,
    component: BookingConfirmation,
  },
  {
    layout: FooterRoutes,
    mobileLayout: EmptyRoutes,
    exact: true,
    path: BOOKINGS_INVITE,
    component: BookingsInvite,
    headerMenuStyles: 'md:h-16 xl:h-24',
  },

  /* ########################################################## */
  /* ### Dynamic two-part routes sorted by specificity ######## */
  /* ########################################################## */
  {
    useNewLayout: true,
    exact: true,
    path: DEAL,
    component: features.dealsPage ? Spinner : NotFoundPage,
  },
  {
    useNewLayout: true,
    exact: true,
    path: EXPLORE_STATE,
    component: Explore,
    withNavbar: false,
  },
  {
    useNewLayout: true,
    exact: true,
    path: SECTION_COURSES,
    component: Courses,
    withNavbar: false,
    withFooter: false,
  },
  /* ########################################################## */
  /* ### Dynamic three-part routes sorted by specificity ###### */
  /* ########################################################## */
  {
    useNewLayout: true,
    exact: true,
    path: CHECKOUT,
    component: Checkout,
  },
  {
    useNewLayout: true,
    exact: true,
    path: RATE_TYPES,
    component: RateTypes,
    preloadComponent: () => SelectProvider.preload(),
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: MEMBERSHIP_UPSELL,
    component: MembershipUpsell,
  },
  {
    useNewLayout: true,
    exact: true,
    path: SELECT_PROVIDER,
    component: SelectProvider,
    preloadComponent: () => Checkout.preload(),
  },
  {
    useNewLayout: true,
    exact: true,
    path: STATE_SEARCH_RESULT,
    component: StateListing,
    withNavbar: false,
    withFooter: false,
    preloadComponent: () => TeeTimes.preload(),
  },
  {
    useNewLayout: true,
    exact: true,
    path: CITY_SEARCH_RESULT,
    component: Courses,
    withNavbar: false,
    withFooter: false,
    preloadComponent: () => TeeTimes.preload(),
  },
  /* ########################################################## */
  /* ### Dynamic four-part routes sorted by specificity ####### */
  /* ########################################################## */
  {
    layout: MySGRoutes,
    exact: true,
    path: BOOKINGS_ID,
    component: BookingConfirmation,
    withFooter: false,
    useMobileLayout: true,
  },
  {
    useNewLayout: true,
    exact: true,
    path: COURSE_SEARCH_RESULT,
    component: TeeTimes,
    preloadComponent: () => RateTypes.preload(),
  },
  {
    useNewLayout: true,
    exact: true,
    path: COURSE_DETAILS,
    component: CourseDetails,
    withFooter: false,
  },
  {
    layout: FooterRoutes,
    exact: true,
    path: UNLOCK_ACCOUNT_FAILED,
    component: UnlockAccountFailed,
    withDownloadsSection: true,
  },
  /* This is catch all error route */
  {
    layout: FooterRoutes,
    exact: false,
    path: NOT_FOUND,
    component: NotFoundPage,
    withDownloadsSection: true,
  },
  {
    layout: FooterRoutes,
    exact: false,
    path: undefined,
    component: NotFoundPage,
    withDownloadsSection: true,
    status: 404,
  },
];

const Routes = () => {
  const { isMobileScreen } = useScreen();
  const { isUnderMaintenance } = useSelector(({ app }) => app);

  return (
    <Suspense fallback={<Loading extraClass="min-h-screen" backgroundColor="bg-white-one" />}>
      <ScrollToTop />
      <Switch>
        {routes.map((route) => {
          const routeKey = `route-sg-${route.path}`;
          const OldLayout = isMobileScreen && route.mobileLayout
            ? route.mobileLayout : route.layout;

          if (isUnderMaintenance) return <UnderMaintenance />;

          if (route?.useNewLayout) {
            const Component = route.component;
            return (
              <Route
                key={routeKey}
                exact={route.exact}
                path={route.path}
                render={(props) => (
                  <NewLayout
                    withNavbar={route?.withNavbar}
                    withFooter={route?.withFooter}
                    navbarProps={route?.navbarProps}
                    footerProps={route?.footerProps}
                  >
                    <Component {...props} preloadComponent={route?.preloadComponent ?? null} />
                  </NewLayout>
                )}
              />
            );
          }

          return (
            <OldLayout
              key={routeKey}
              exact={route.exact}
              path={route.path}
              component={route.component}
              withDownloadsSection={route.withDownloadsSection}
              withSubScenery={route.withSubScenery}
              pageStatus={route.status}
              headerMenuStyles={route.headerMenuStyles || ''}
              HeaderMenu={HeaderMenu}
              backTo={isMobileScreen ? (route.backToMobile || route.backTo) : route.backTo}
              useMobileLayout={route.useMobileLayout}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
};

export default Routes;
